// Cross-service time-overlap detection (lifted verbatim from the widget's
// state/conflicts.ts in parity Phase 0.5 — pure TS, no client needed).
//
// A cart can hold items from any mix of services (dining + spa + tasting +
// events). Each item carries `starts_at` + `ends_at` ISO timestamps in its
// metadata. We treat each item as a half-open interval [starts_at, ends_at)
// and consider any two items in conflict if their intervals overlap —
// regardless of service type (except accommodation, a multi-day stay, which
// is exempt: activities during your stay are meant to overlap it).
//
// Detection is intentionally permissive (soft warn, not hard block).

/** Structural view of a cart line — the widget's CartItem satisfies it. */
export interface ConflictableItem {
  id: string;
  description: string;
  serviceId?: string | null;
  serviceType?: string;
  metadata?: Record<string, unknown>;
}

export interface BusyInterval {
  itemId: string;
  description: string;
  start: number; // epoch ms
  end: number;
  // Event id for event-sourced items (a ticket + its add-ons share one).
  // Used to suppress false "overlap" warnings between a ticket and its own
  // add-ons, which necessarily share the same time window.
  eventId: string | null;
  // Service id + start together identify a "seating group": a party splitting
  // into different products of the SAME service at the SAME slot (e.g. two
  // wine-tasting flights side by side at 11:00) is concurrent by design, not
  // "in two places at once". Used to suppress that false overlap.
  serviceId: string | null;
}

/**
 * Extract [start, end) intervals from cart items with a usable starts_at.
 * Missing or invalid ends_at falls back to a 60-minute window. Pass
 * `excludeItemId` to drop the currently-being-edited item.
 */
export function getBusyIntervals(
  items: readonly ConflictableItem[],
  excludeItemId?: string | null,
): BusyInterval[] {
  const out: BusyInterval[] = [];
  for (const item of items) {
    if (excludeItemId && item.id === excludeItemId) continue;
    // Accommodation (a multi-day stay) is meant to coexist with everything you
    // do during it — a tasting or dinner "overlapping" your stay is the point,
    // not a conflict. So a stay never participates in time-overlap detection.
    if (item.serviceType === "accommodation") continue;
    const startsRaw = item.metadata?.starts_at;
    const endsRaw = item.metadata?.ends_at;
    if (typeof startsRaw !== "string") continue;
    // ChiDesk, native bookings and events all store real ISO instants.
    // Preserve their Z/offset so every service is compared on the same UTC
    // timeline, independent of the browser's timezone. Removing ChiDesk's Z
    // shifted a 12:45–14:45 SAST treatment to 10:45–12:45 SAST and hid its
    // overlap with a 13:00 lunch. tz_offset_minutes is for display only.
    const start = Date.parse(startsRaw);
    if (!Number.isFinite(start)) continue;
    let end = typeof endsRaw === "string" ? Date.parse(endsRaw) : NaN;
    if (!Number.isFinite(end) || end <= start) {
      // Fallback: assume a 60-minute window so we still detect overlap on
      // items missing ends_at (e.g. legacy free restaurant reservations).
      end = start + 60 * 60 * 1000;
    }
    const eventId =
      item.metadata?.source === "event" &&
      typeof item.metadata?.event_id === "string"
        ? (item.metadata.event_id as string)
        : null;
    out.push({
      itemId: item.id,
      description: item.description,
      start,
      end,
      eventId,
      serviceId: item.serviceId ?? null,
    });
  }
  return out;
}

/**
 * Returns the first busy interval that overlaps [start, end), or null.
 * Half-open: an item ending at 19:00 doesn't overlap one starting at 19:00.
 */
export function findOverlap(
  intervals: readonly BusyInterval[],
  start: number,
  end: number,
): BusyInterval | null {
  for (const i of intervals) {
    if (i.start < end && start < i.end) return i;
  }
  return null;
}

/**
 * Given a candidate window + the cart's busy intervals, suggest the earliest
 * same-day slot of equal length that does NOT overlap any busy interval.
 * Returns null if no slot fits before the day window closes.
 */
export function suggestNextFreeSlot(
  intervals: readonly BusyInterval[],
  candidateStart: number,
  candidateEnd: number,
  opts?: {
    gridMinutes?: number;
    dayStartHour?: number;
    dayEndHour?: number;
    // Bookable windows (epoch ms) for the candidate's day, from the service's
    // operating hours. When supplied, suggestions never fall outside these;
    // when omitted, falls back to dayStartHour–dayEndHour (generic 07:00–23:00).
    openWindows?: readonly { start: number; end: number }[];
  },
): number | null {
  const grid = (opts?.gridMinutes ?? 30) * 60 * 1000;
  const duration = candidateEnd - candidateStart;
  if (duration <= 0) return null;

  const sorted = [...intervals].sort((a, b) => a.start - b.start);
  const fitsFree = (t: number) => !findOverlap(sorted, t, t + duration);

  // Resolve the day's bookable windows: real operating hours if provided,
  // otherwise a generic day window so we still suggest *something*.
  let windows: { start: number; end: number }[];
  if (opts?.openWindows && opts.openWindows.length > 0) {
    windows = [...opts.openWindows].sort((a, b) => a.start - b.start);
  } else {
    const d = new Date(candidateStart);
    const ds = new Date(d);
    ds.setHours(opts?.dayStartHour ?? 7, 0, 0, 0);
    const de = new Date(d);
    de.setHours(opts?.dayEndHour ?? 23, 0, 0, 0);
    windows = [{ start: ds.getTime(), end: de.getTime() }];
  }

  // Forward: within each window, walk from the candidate (or the window's
  // open time) and snap past any collisions. Never crosses the window's close.
  for (const w of windows) {
    let cursor = roundUpToGrid(Math.max(candidateStart, w.start), grid);
    while (cursor + duration <= w.end) {
      const collision = findOverlap(sorted, cursor, cursor + duration);
      if (!collision) return cursor;
      cursor = roundUpToGrid(collision.end, grid);
    }
  }

  // Backward: latest free slot at/before the candidate that still fits a window.
  for (let wi = windows.length - 1; wi >= 0; wi--) {
    const w = windows[wi]!;
    const from = Math.min(
      roundDownToGrid(candidateStart, grid),
      roundDownToGrid(w.end - duration, grid),
    );
    for (let t = from; t >= w.start; t -= grid) {
      if (t + duration <= w.end && fitsFree(t)) return t;
    }
  }

  return null;
}

function roundUpToGrid(ms: number, grid: number): number {
  return Math.ceil(ms / grid) * grid;
}
function roundDownToGrid(ms: number, grid: number): number {
  return Math.floor(ms / grid) * grid;
}

/**
 * Format an epoch-ms timestamp as a short "Tue, 21 May · 18:30" style for
 * inline suggestion buttons (en-ZA to match the rest of the platform).
 */
export function formatSuggestion(ms: number): string {
  return new Date(ms).toLocaleString("en-ZA", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}
